/**
 * Portierter Kern von `jscanify` (https://github.com/puffinsoft/jscanify),
 * MIT-Lizenz, © ColonelParrot und Mitwirkende - bewusst als eigene Datei
 * vendored statt als npm-Abhängigkeit eingebunden: das npm-Paket deklariert
 * `canvas` (natives Node-Addon, für serverseitige Nutzung) als harte
 * Abhängigkeit, obwohl wir ausschließlich den browserseitigen Algorithmus
 * brauchen. Ein `npm install` im Docker-Build (`node:22-alpine`) würde
 * versuchen, `canvas` zu kompilieren - ohne die dafür nötigen
 * Cairo/Pango-Bibliotheken auf Alpine/musl schlägt das fehl und würde den
 * gesamten Frontend-Build brechen. Diese Datei enthält nur den reinen
 * Algorithmus (Canny-Kantenerkennung, Konturfindung, Eckpunkt-Auswahl,
 * Perspektiven-Entzerrung über `cv.warpPerspective`), unverändert
 * übernommen, und erwartet - wie das Original - `cv` (OpenCV.js) als
 * Parameter statt als globale Variable.
 */

export interface CvCornerPoint {
  x: number;
  y: number;
}

export interface CvCorners {
  topLeftCorner: CvCornerPoint;
  topRightCorner: CvCornerPoint;
  bottomLeftCorner: CvCornerPoint;
  bottomRightCorner: CvCornerPoint;
}

function distance(p1: CvCornerPoint, p2: CvCornerPoint): number {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

export class JScanifyCore {
  constructor(private readonly cv: any) {}

  /**
   * Findet das fotografierte Blatt Papier im Bild. Nimmt bewusst NICHT
   * einfach die größte Kontur (das greift bei unruhigem Hintergrund -
   * Tischmaserung, andere Gegenstände, Hand im Bild - oft eine Kontur, die
   * Papier UND Umgebung zusammenfasst und weit über das tatsächliche Blatt
   * hinausreicht), sondern nur Konturen, die sich zu einem konvexen VIERECK
   * vereinfachen lassen (`approxPolyDP`) - das entspricht der Form eines
   * Blatts Papier zuverlässiger als "größte Fläche" allein. Liefert `null`,
   * wenn keine passende Kontur gefunden wird; der Aufrufer fällt dann auf
   * einen festen Rand-Fallback zurück statt eine falsch erkannte Fläche zu übernehmen.
   */
  findPaperContour(img: any): any {
    const cv = this.cv;
    const imgGray = new cv.Mat();
    cv.Canny(img, imgGray, 50, 200);

    const imgBlur = new cv.Mat();
    cv.GaussianBlur(imgGray, imgBlur, new cv.Size(3, 3), 0, 0, cv.BORDER_DEFAULT);

    const imgThresh = new cv.Mat();
    cv.threshold(imgBlur, imgThresh, 0, 255, cv.THRESH_OTSU);

    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();
    cv.findContours(imgThresh, contours, hierarchy, cv.RETR_CCOMP, cv.CHAIN_APPROX_SIMPLE);

    // Kleinere Konturen als 15% der Bildfläche sind fast immer Rauschen
    // (Schrift, Kanten kleiner Objekte) statt des fotografierten Blatts.
    const minArea = img.rows * img.cols * 0.15;
    let bestQuad: any = null;
    let bestArea = 0;

    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i);
      const area = cv.contourArea(contour);
      if (area < minArea) {
        contour.delete();
        continue;
      }

      const peri = cv.arcLength(contour, true);
      const approx = new cv.Mat();
      cv.approxPolyDP(contour, approx, 0.02 * peri, true);

      if (approx.rows === 4 && cv.isContourConvex(approx) && area > bestArea) {
        if (bestQuad) bestQuad.delete();
        bestQuad = approx.clone();
        bestArea = area;
      }

      approx.delete();
      contour.delete();
    }

    imgGray.delete();
    imgBlur.delete();
    imgThresh.delete();
    contours.delete();
    hierarchy.delete();
    return bestQuad;
  }

  /** Bestimmt die 4 Eckpunkte einer Kontur relativ zu deren `minAreaRect`-Mittelpunkt. */
  getCornerPoints(contour: any): CvCorners {
    const cv = this.cv;
    const rect = cv.minAreaRect(contour);
    const center = rect.center;

    let topLeftCorner: CvCornerPoint | undefined;
    let topLeftCornerDist = 0;
    let topRightCorner: CvCornerPoint | undefined;
    let topRightCornerDist = 0;
    let bottomLeftCorner: CvCornerPoint | undefined;
    let bottomLeftCornerDist = 0;
    let bottomRightCorner: CvCornerPoint | undefined;
    let bottomRightCornerDist = 0;

    for (let i = 0; i < contour.data32S.length; i += 2) {
      const point = { x: contour.data32S[i], y: contour.data32S[i + 1] };
      const dist = distance(point, center);
      if (point.x < center.x && point.y < center.y) {
        if (dist > topLeftCornerDist) {
          topLeftCorner = point;
          topLeftCornerDist = dist;
        }
      } else if (point.x > center.x && point.y < center.y) {
        if (dist > topRightCornerDist) {
          topRightCorner = point;
          topRightCornerDist = dist;
        }
      } else if (point.x < center.x && point.y > center.y) {
        if (dist > bottomLeftCornerDist) {
          bottomLeftCorner = point;
          bottomLeftCornerDist = dist;
        }
      } else if (point.x > center.x && point.y > center.y) {
        if (dist > bottomRightCornerDist) {
          bottomRightCorner = point;
          bottomRightCornerDist = dist;
        }
      }
    }

    return {
      topLeftCorner: topLeftCorner as CvCornerPoint,
      topRightCorner: topRightCorner as CvCornerPoint,
      bottomLeftCorner: bottomLeftCorner as CvCornerPoint,
      bottomRightCorner: bottomRightCorner as CvCornerPoint,
    };
  }

  /** Entzerrt/schneidet das Bild anhand der 4 Eckpunkte auf `resultWidth`x`resultHeight` zu (Perspektiventransformation). */
  extractPaper(image: HTMLCanvasElement, resultWidth: number, resultHeight: number, cornerPoints: CvCorners): HTMLCanvasElement {
    const cv = this.cv;
    const canvas = document.createElement('canvas');
    const img = cv.imread(image);

    const { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner } = cornerPoints;

    const dsize = new cv.Size(resultWidth, resultHeight);
    const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
      topLeftCorner.x,
      topLeftCorner.y,
      topRightCorner.x,
      topRightCorner.y,
      bottomLeftCorner.x,
      bottomLeftCorner.y,
      bottomRightCorner.x,
      bottomRightCorner.y,
    ]);
    const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, resultWidth, 0, 0, resultHeight, resultWidth, resultHeight]);

    const M = cv.getPerspectiveTransform(srcTri, dstTri);
    const warpedDst = new cv.Mat();
    cv.warpPerspective(img, warpedDst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());

    cv.imshow(canvas, warpedDst);

    img.delete();
    warpedDst.delete();
    srcTri.delete();
    dstTri.delete();
    M.delete();
    return canvas;
  }
}
